import { get, del } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";
import { fetchAuthSession } from "aws-amplify/auth";
import config from "../config";

const apiName = "vc-proposal-generator";
const path = "/proposal-generator/";

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return {
        Authorization: `Bearer ${session.tokens.idToken.toString()}`,
      };
    }
    return {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

export async function getSubmissions(section = 1) {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "all",
        section: `${section}`,
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getAdditionalSubmissions(section = 1, lastkey = "") {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "additionalSubmissions",
        section: `${section}`,
        lastkey: lastkey,
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getS3FormsFileUrl(FilePath, exp = 60) {
  if (
    (typeof FilePath === "string" || FilePath instanceof String) &&
    FilePath !== ""
  ) {
    let result = await getUrl({
      path: FilePath,
      options: {
        expiresIn: exp,
      },
    });
    return result.url;
  } else {
    return null;
  }
}

export async function getProposalTypes() {
  // `types` (prod) vs `devtypes` (test/dev) — driven by the single deploy
  // switch in config.js (REACT_APP_DEPLOY_ENV). Don't hand-edit this.
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: config.proposalTypesType,
      },
    },
  }).response;

  // The API returns the response as-is, so we just return what we get
  const result = await response.body.json();
  return result;
}

export async function getRegistrationTypes() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "registrations",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getRegistrationCategoryTypes() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "registrationcategories",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getCustodians() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "custodians",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getCustomInfo() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "custom",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getLogos() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "logo",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getDocumentsTree() {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "docs",
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

// Fee responses depend on request headers while all three lookups share one
// GET URL. Give each lookup a distinct URL so an intermediary cannot reuse a
// stale fee response for another advisor, strategy, or tier.
let feeRequestSequence = 0;
const feeQueryParams = () => ({
  feeRequest: `${Date.now()}-${++feeRequestSequence}`,
});

export async function getCustomFees(advisor = "", strategy = "") {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      queryParams: feeQueryParams(),
      headers: {
        ...authHeaders,
        type: "custom-fees",
        // Advisor names can carry non-ASCII glyphs (e.g. "Chad Chubb, CFP®,
        // CSLP®"). HTTP headers are ASCII-only, so send it percent-encoded —
        // the backend unquote()s it before the proposals-custom-fees query.
        advisor: encodeURIComponent(advisor),
        strategy: encodeURIComponent(strategy),
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getManagementFees(strategy = "", tier = "") {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      queryParams: feeQueryParams(),
      headers: {
        ...authHeaders,
        type: "management-fees",
        strategy: encodeURIComponent(strategy),
        tier: encodeURIComponent(tier),
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getManagementFeeTiers(strategy = "") {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      queryParams: feeQueryParams(),
      headers: {
        ...authHeaders,
        type: "management-fee-tiers",
        strategy: encodeURIComponent(strategy),
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function deleteSubmissions(dateTime, username) {
  const authHeaders = await getAuthHeaders();
  let response = await del({
    apiName,
    path: path,
    options: {
      headers: {
        ...authHeaders,
      },
      body: {
        dateTime: dateTime,
        username: username,
      },
    },
  }).response;

  const result = await response.body.json();
  const status = Number(result?.statusCode);
  if (Number.isFinite(status) && status >= 400) {
    let details = result?.body;
    if (typeof details === "string") {
      try {
        details = JSON.parse(details);
      } catch (error) {
        // Keep the plain response text as the error message.
      }
    }
    const message =
      (typeof details === "string" && details) ||
      details?.error ||
      details?.message ||
      result?.error ||
      "The submission could not be deleted.";
    const error = new Error(message);
    error.status = status;
    throw error;
  }
  return result;
}

export async function getFile(dateTime, username, section = 0, fileType = "") {
  const authHeaders = await getAuthHeaders();
  const headers = {
    ...authHeaders,
    dateTime: dateTime,
    username: username,
    section: `${section}`,
  };
  if (fileType) {
    headers.filetype = fileType;
  }
  let response = await get({
    apiName,
    path: path + "get-file",
    options: { headers },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getS3File(bucket, prefixfilename, section = 0) {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-s3-file",
    options: {
      headers: {
        ...authHeaders,
        bucket: bucket,
        prefixfilename: prefixfilename,
        section: `${section}`,
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getS3FileList(bucket, prefix) {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-s3-list",
    options: {
      headers: {
        ...authHeaders,
        bucket: bucket,
        prefix: prefix,
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}

export async function getZipFile(userName, dateTime, prefixList = []) {
  const authHeaders = await getAuthHeaders();
  let response = await get({
    apiName,
    path: path + "get-data",
    options: {
      headers: {
        ...authHeaders,
        type: "zip",
        prefix: prefixList,
        section: `${userName}_${dateTime}`,
      },
    },
  }).response;

  const result = await response.body.json();
  return result;
}
